import { NgModule, ErrorHandler, Optional, SkipSelf } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { CoreRoutingModule } from './core-routing.module';
import { LoggerModule } from 'ngx-logger';

import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { LoginComponent } from './login/login.component';
import { environment } from '../../environments/environment';
import { CoreComponent } from './core.component';

import { SharedModule } from '../shared/shared.module';
import { UIErrorHandler } from './error-handler';

import { TokenInterceptor } from './interceptors/token.interceptor';
import { HeaderInterceptor } from './interceptors/header-interceptor';
import { CacheInterceptor } from './interceptors/cache-interceptor';
import { LoggingInterceptor } from './interceptors/logging-interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { AuthService } from './services/auth.service';
import { StorageService } from './services/storage.service';
import { LogoutComponent } from './logout/logout.component';
import { NotAuthorizedComponent } from './not-authorized/not-authorized.component';
import { CacheService } from './services/cache.service';
import { LoggingApiService } from './services/logging-api.service';
import { LoggingMessageService } from './services/logging-message.service';
import { WindowEventsService } from './services/window-events.service';
import { PendoService } from './services/pendo.service';
import { BlankPageComponent } from './blank-page/blank-page.component';
import { SiteManagementService } from './services/site-management.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService } from 'primeng/api';
import { SiteCoordinatorComponent } from './site-management/site-coordinator/site-coordinator.component';
import { LeapFrogReportComponent } from './reports/leap-frog-report/leap-frog-report.component';
import { FacilityDemographicsComponent } from './site-management/facility-demographics/facility-demographics.component';
import { MenuAccessComponent } from './site-management/site-coordinator/menu-access/menu-access.component';
import { DataEntryAccessComponent } from './site-management/site-coordinator/data-entry-access/data-entry-access.component';
import { GridModule } from '@progress/kendo-angular-grid';
import { UserManagementComponent } from './site-management/manage-users/user-management.component';
import { AddressComponent } from './site-management/facility-demographics/address/address.component';
import { ChiefNurseComponent } from './site-management/facility-demographics/chief-nurse/chief-nurse.component';
import { MagnetComponent } from './site-management/facility-demographics/magnet/magnet.component';
import { DemographicsComponent } from './site-management/facility-demographics/demographics/demographics.component';
import { EmergencyServicesComponent } from './site-management/facility-demographics/emergency-services/emergency-services.component';
import { PerinatalCareComponent } from './site-management/facility-demographics/perinatal-care/perinatal-care.component';
import { PathwayComponent } from './site-management/facility-demographics/pathway/pathway.component';
import { CensusComponent } from './site-management/facility-demographics/census/census.component';
import { RnEducationComponent } from './reports/leap-frog-report/rn-education/rn-education.component';
import { StaffingSkillLevelComponent } from './reports/leap-frog-report/staffing-skill-level/staffing-skill-level.component';
import { FacilityExportComponent } from './facility-export/facility-export.component';
import { FacilityExportHistoryComponent } from './facility-export/facility-export-history/facility-export-history.component';
import { FacilityExportFileStatusComponent } from './facility-export/facility-export-file-status/facility-export-file-status.component';
import { FacilityExportActivityLogComponent } from './facility-export/facility-export-activity-log/facility-export-activity-log.component';
import { FacilityExportOptionsComponent } from './facility-export/facility-export-options/facility-export-options.component';
import { NgxPaginationModule } from 'ngx-pagination';

// All services which have to have one and only one instance per application (singleton services) should be implemented here.
// Typical example can be authentication service or user service

@NgModule({
  declarations: [
    CoreComponent,
    PageNotFoundComponent,
    LoginComponent,
    LogoutComponent,
    NotAuthorizedComponent,
    BlankPageComponent,
    SiteCoordinatorComponent,
    FacilityDemographicsComponent,
    MenuAccessComponent,
    DataEntryAccessComponent,
    UserManagementComponent,
    AddressComponent,
    ChiefNurseComponent,
    MagnetComponent,
    DemographicsComponent,
    EmergencyServicesComponent,
    PerinatalCareComponent,
    PathwayComponent,
    CensusComponent,
    LeapFrogReportComponent,
    RnEducationComponent,
    StaffingSkillLevelComponent,
    FacilityExportComponent,
    FacilityExportHistoryComponent,
    FacilityExportFileStatusComponent,
    FacilityExportActivityLogComponent,
    FacilityExportOptionsComponent
  ],
  imports: [
    CoreRoutingModule,
    GridModule,
    SharedModule,
    ConfirmDialogModule,
    NgxPaginationModule,
    LoggerModule.forRoot({
      serverLoggingUrl: environment.LogService,
      level: environment.LogLevel,
      serverLogLevel: environment.ServerLogLevel,
      disableConsoleLogging: environment.DisableConsoleLogging,
    }),
    RouterModule.forRoot([])
  ],
  exports: [
    RouterModule,
    SharedModule,
    CoreComponent,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true},
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
    { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: LoadingScreenInterceptor, multi: true },
    { provide: ErrorHandler, useClass: UIErrorHandler },
    AuthService,
    LoggingApiService,
    LoggingMessageService,
    CacheService,
    StorageService,
    PendoService,
    WindowEventsService,
    SiteManagementService,
    ConfirmationService,
    MessageService,
  ]
})

export class CoreModule {
  /**
   * Ensure it can only be imported once
   */
  constructor(@Optional() @SkipSelf() core: CoreModule) {
    if (core) {
      throw new Error('You shall not run!');
    }
  }
}
